import React from 'react';
import { Calculator, Info } from 'lucide-react';

/**
 * Секция выбора модели расчета для оценки риска банкротства
 */
const CalculationModelSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  bankruptcyModels
}) => {
  // Получение информации о выбранной модели
  const getModelDescription = (modelId) => {
    switch (modelId) {
      case 'altman-z':
        return {
          title: 'Модель Альтмана (Z-счет)',
          description: 'Классическая модель оценки вероятности банкротства, разработанная Эдвардом Альтманом в 1968 году. Учитывает 5 ключевых финансовых коэффициентов, взвешенных с определенными весами.',
          advantages: 'Высокая точность для производственных компаний, проверена временем, простота интерпретации.',
          limitations: 'Менее точна для малых предприятий, непроизводственных компаний и компаний из развивающихся рынков.'
        };
      case 'fulmer':
        return {
          title: 'Модель Фулмера',
          description: 'Модель, разработанная для оценки банкротства малого бизнеса. Включает 9 финансовых коэффициентов с различными весами.',
          advantages: 'Хорошо подходит для малых предприятий, учитывает больше показателей.',
          limitations: 'Сложнее в расчете, требует более детальных финансовых данных.'
        };
      case 'springate':
        return {
          title: 'Модель Спрингейта',
          description: 'Модель, разработанная на основе пошагового дискриминантного анализа. Использует 4 финансовых коэффициента.',
          advantages: 'Простота применения, хорошая точность для различных отраслей.',
          limitations: 'Ограниченная применимость для сферы услуг, не учитывает рыночные факторы.'
        };
      case 'taffler':
        return {
          title: 'Модель Таффлера',
          description: 'Британская модель, разработанная для оценки компаний, торгующихся на бирже. Использует 4 финансовых коэффициента.',
          advantages: 'Хорошо подходит для оценки публичных компаний, проста в расчете.',
          limitations: 'Менее точна для частных компаний и малого бизнеса.'
        };
      case 'beaver':
        return {
          title: 'Модель Бивера',
          description: 'Одномерная модель, основанная на анализе отдельных финансовых коэффициентов без их взвешивания.',
          advantages: 'Простота расчета, возможность оценки по отдельным показателям.',
          limitations: 'Не дает единого интегрального показателя, требует экспертной интерпретации.'
        };
      case 'saifullin-kadykov':
        return {
          title: 'Модель Сайфуллина-Кадыкова',
          description: 'Российская модель, адаптированная к особенностям отечественной экономики и бухгалтерской отчетности.',
          advantages: 'Учитывает специфику российских предприятий, использует показатели из стандартной отчетности.',
          limitations: 'Ограниченная применимость для международных компаний, не учитывает отраслевую специфику.'
        };
      case 'comprehensive':
        return {
          title: 'Комплексная оценка',
          description: 'Комбинированный подход, использующий несколько моделей с учетом их взвешенных результатов и качественных факторов.',
          advantages: 'Наиболее точная оценка, учитывающая преимущества разных моделей и нефинансовые факторы.',
          limitations: 'Сложность расчета, требует большего объема входных данных.'
        };
      default:
        return {
          title: 'Выберите модель',
          description: 'Пожалуйста, выберите модель оценки риска банкротства.',
          advantages: '',
          limitations: ''
        };
    }
  };

  const selectedModel = getModelDescription(formData.calculationModel);

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Модель оценки
      </h3>
      
      <div className="mb-4">
        <label htmlFor="calculationModel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Выберите модель для расчета
        </label>
        <select
          id="calculationModel"
          name="calculationModel"
          value={formData.calculationModel}
          onChange={handleInputChange}
          className={`form-select w-full ${errors.calculationModel ? 'input-error' : ''}`}
        >
          <option value="">Выберите модель оценки</option>
          {bankruptcyModels.map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>
        {errors.calculationModel && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.calculationModel}
          </p>
        )}
      </div>

      {/* Информация о выбранной модели */}
      {formData.calculationModel && (
        <div className="bg-white dark:bg-gray-800 rounded-md p-3 border border-blue-100 dark:border-blue-800">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
            <Info className="w-4 h-4 mr-1 text-blue-500" />
            {selectedModel.title}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            {selectedModel.description}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="text-xs">
              <p className="font-medium text-green-600 dark:text-green-400 mb-1">Преимущества:</p>
              <p className="text-gray-600 dark:text-gray-400">{selectedModel.advantages}</p>
            </div>
            <div className="text-xs">
              <p className="font-medium text-orange-600 dark:text-orange-400 mb-1">Ограничения:</p>
              <p className="text-gray-600 dark:text-gray-400">{selectedModel.limitations}</p>
            </div>
          </div>
        </div>
      )}

      {/* Рекомендации по выбору модели */}
      <div className="mt-4 text-xs text-gray-600 dark:text-gray-400">
        <p className="mb-1 font-medium">Рекомендации по выбору модели:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Для <span className="font-medium">производственных компаний</span> рекомендуются модели Альтмана и Спрингейта</li>
          <li>Для <span className="font-medium">малого бизнеса</span> лучше подходят модели Фулмера и Сайфуллина-Кадыкова</li>
          <li>Для <span className="font-medium">торговых компаний</span> используйте модель Таффлера</li>
          <li>Для <span className="font-medium">наиболее точной оценки</span> рекомендуется использовать комплексную оценку</li>
        </ul>
      </div>
    </div>
  );
};

export default CalculationModelSection;